import { parseCsvContent, type CsvDelimiterMode } from '@/utils/csvImport';

export function parseNumericValue(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '');
  if (trimmed === '') {
    return null;
  }
  const normalized = trimmed.replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
    return null;
  }
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

/** Calendar year: integer only, no date/time fragments. */
export function parseCalendarYear(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d{4}$/.test(trimmed)) {
    return null;
  }
  const year = Number(trimmed);
  if (!Number.isInteger(year) || year < 1000 || year > 3000) {
    return null;
  }
  return year;
}

export function validateConsecutiveYears(years: number[]): string | null {
  if (years.length === 0) {
    return 'No year values found.';
  }

  const sorted = [...years].sort((a, b) => a - b);
  const duplicates = new Set<number>();
  const seen = new Set<number>();
  sorted.forEach((year) => {
    if (seen.has(year)) {
      duplicates.add(year);
    }
    seen.add(year);
  });
  if (duplicates.size > 0) {
    return `Duplicate years found: ${[...duplicates].join(', ')}.`;
  }

  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i] - sorted[i - 1] !== 1) {
      return `Years must be consecutive without gaps (missing years between ${sorted[i - 1]} and ${sorted[i]}).`;
    }
  }

  return null;
}

export interface ParsedCsvTable {
  headers: string[];
  rows: string[][];
  delimiterMode: CsvDelimiterMode;
}

export function parseCsvTable(text: string, delimiterMode: CsvDelimiterMode = 'auto'): ParsedCsvTable | null {
  const rows = parseCsvContent(text, delimiterMode);
  if (rows.length < 2) {
    return null;
  }
  return {
    headers: rows[0],
    rows: rows.slice(1),
    delimiterMode,
  };
}

function getColumnValues(table: ParsedCsvTable, columnIndex: string): string[] {
  const index = Number(columnIndex);
  if (Number.isNaN(index) || index < 0) {
    return [];
  }
  return table.rows.map((row) => row[index] ?? '');
}

export interface LayerProductionImportRow {
  year: number;
  oil: number | null;
  gas: number | null;
  water: number | null;
}

export function buildLayerProductionImportRows(
  table: ParsedCsvTable,
  mapping: {
    yearColumn: string;
    oilColumn: string;
    gasColumn: string;
    waterColumn: string;
  },
): { rows: LayerProductionImportRow[]; error: string | null } {
  if (!mapping.yearColumn) {
    return { rows: [], error: 'Select the year column.' };
  }
  if (!mapping.oilColumn && !mapping.gasColumn) {
    return { rows: [], error: 'Select at least one production column: oil or gas.' };
  }

  const yearValues = getColumnValues(table, mapping.yearColumn);
  const years: number[] = [];
  for (let i = 0; i < yearValues.length; i += 1) {
    const year = parseCalendarYear(yearValues[i]);
    if (year === null) {
      return { rows: [], error: `Invalid year at row ${i + 2}: "${yearValues[i]}". Use calendar year only (e.g. 2020).` };
    }
    years.push(year);
  }

  const yearError = validateConsecutiveYears(years);
  if (yearError) {
    return { rows: [], error: yearError };
  }

  const oilValues = mapping.oilColumn ? getColumnValues(table, mapping.oilColumn) : [];
  const gasValues = mapping.gasColumn ? getColumnValues(table, mapping.gasColumn) : [];
  const waterValues = mapping.waterColumn ? getColumnValues(table, mapping.waterColumn) : [];

  const rows: LayerProductionImportRow[] = [];
  for (let i = 0; i < years.length; i += 1) {
    const oil = mapping.oilColumn ? parseNumericValue(oilValues[i] ?? '') : null;
    const gas = mapping.gasColumn ? parseNumericValue(gasValues[i] ?? '') : null;
    const water = mapping.waterColumn ? parseNumericValue(waterValues[i] ?? '') : null;

    if (mapping.oilColumn && (oilValues[i] ?? '').trim() !== '' && oil === null) {
      return { rows: [], error: `Invalid oil value at row ${i + 2}: "${oilValues[i]}".` };
    }
    if (mapping.gasColumn && (gasValues[i] ?? '').trim() !== '' && gas === null) {
      return { rows: [], error: `Invalid gas value at row ${i + 2}: "${gasValues[i]}".` };
    }
    if (mapping.waterColumn && (waterValues[i] ?? '').trim() !== '' && water === null) {
      return { rows: [], error: `Invalid water value at row ${i + 2}: "${waterValues[i]}".` };
    }

    rows.push({ year: years[i], oil, gas, water });
  }

  return { rows, error: null };
}
